<template>
  <div>
    <div
      class="menu-list-item ellipsis ignore-fix-focus"
      :class="[type, { disabled: disabled }]"
      data-qa="menu-list-item"
      @click="itemClick($event, !selected)"
      @keypress.space.prevent
      @keyup.space="itemClick($event, !selected)"
      @keydown.enter="itemClick($event, !selected)"
      @focus="emit('focus')"
      ref="menuListItem"
      tabindex="0"
      v-b-tooltip="Utils.elementTreeOverflow(menuListItem) ? title : ''"
    >
      <form-checkbox
        v-if="type === 'multi-select'"
        tabindex="-1"
        :id="getCheckboxId()"
        :checked="selected"
        data-qa="menu-list-item-checkbox"
        :indeterminate="indeterminate"
      />
      <div v-if="['single-select'].includes(type)" class="icon-check-holder">
        <icon-check v-if="selected" class="icon-check" color="stratos" />
      </div>
      <div v-if="icon" class="icon-wrapper">
        <component :is="menuIcon" />
      </div>
      <span
        class="ellipsis is-fullwidth item-text"
        ref="menuListItemText"
        :class="{ checked: selected }"
      >
        <span class="item-title" :class="`rbz-text-${titleVariant}`"> {{ title }} </span>&nbsp;<span
          class="item-subtitle rbz-text-small"
        >
          {{ subtitle }}
        </span>
      </span>
      <span v-if="$slots['trailing-icon']" class="trailing-icon">
        <slot name="trailing-icon" />
      </span>
    </div>
    <div v-if="type === 'multi-select'" class="nested-items">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import FormCheckbox from './FormCheckbox.vue';
import { replace } from 'lodash';
import { computed, ref, useTemplateRef } from 'vue';
import IconCheck from '../../assets/icons/IconCheck.vue';
import { getIconComponent } from '../../assets/icons';
import Utils from '../../helpers/Utils';
import DatasetsUtils from '../../helpers/DatasetsUtils';
import { GenericObject } from '../../rbz/types';

export type ListItemType = 'menu' | 'single-select' | 'multi-select';

interface MenuListItemProps {
  title: string;
  titleVariant?: 'regular' | 'medium' | 'small' | 'xsmall';
  icon?: GenericObject;
  subtitle?: string;
  type?: ListItemType;
  classes?: string;
  selected?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  virtualScroll?: boolean;
}

const props = withDefaults(defineProps<MenuListItemProps>(), {
  subtitle: '',
  type: 'menu',
  titleVariant: 'regular',
  icon: null,
  classes: '',
  selected: false,
  disabled: false,
});

interface MenuListItemEmits {
  (e: 'item-click', selected: boolean): void;

  (e: 'focus'): void;
}

const emit = defineEmits<MenuListItemEmits>();

const randomId = DatasetsUtils.generateUUID2();
const getCheckboxId = () => {
  return `${randomId}-${replace(props.title, ' ', '-')}`.toLowerCase();
};

const menuListItem = useTemplateRef<HTMLDivElement>('menuListItem');
const menuListItemText = ref(null);

const itemClick = (event: Event, checked: boolean) => {
  if (event) {
    if (!event.elementsClicked) {
      event.elementsClicked = [];
    }
    event.elementsClicked.push('menuListItem');
  }
  emit('item-click', checked);
};

const menuIcon = computed(() => {
  if (props.icon && typeof props.icon === 'string') {
    return getIconComponent(props.icon);
  }
  return '';
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.menu-list-item {
  align-items: center;
  background-color: map.get(colors.$colors, 'white');
  border-radius: 4px;
  color: map.get(colors.$colors, 'stratos');
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  gap: 4px;
  height: 40px;
  line-height: 18px;
  list-style: none;
  max-width: 100%;
  min-width: 250px;
  padding: 11px 40px 11px 11px;
  position: relative;

  &.dropdown {
    border: 1px solid map.get(colors.$colors, 'silver');
    border-radius: 4px;
  }

  &:hover {
    background-color: map.get(colors.$colors, 'white-ice');
  }

  &:active {
    background-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');

    :deep(.icon-check-holder .icon-check) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:focus:not(:active) {
    box-shadow: inset 0 0 0 2px map.get(colors.$colors, 'magic-mint');
    outline: none;
  }
}

.single-select {
  padding: 8px 11px;

  .icon-check-holder {
    min-height: 24px;
    min-width: 24px;
  }
}

:deep(.multi-select) {
  .checkbox-holder {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 18px;
    min-width: 18px;

    .checkbox-style {
      pointer-events: none;
    }
  }
}

.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.dropdown-menu {
  display: block;
  margin: 0;
  padding: 0;
  position: relative;
}

.nested-items {
  margin: 0;
  padding-left: 15px;
}

.item-subtitle {
  color: map.get(colors.$colors, 'dusty-gray');
}

.icon-wrapper {
  display: flex;

  svg {
    width: 15px;
    height: 15px;
  }
}

.trailing-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}
</style>
